import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { useAdminDraft } from "@contexts/admin/AdminDraftsProvider";
import { SearchOrigin } from "@pages/AdminDashboard/accountFromSearch";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { phoneSearchFormLayout } from "@pages/AdminDashboard/theme/phoneSearchForm";
import { Banner } from "@pages/AdminDashboard/ui/Banner";
import { Button } from "@pages/AdminDashboard/ui/Button";
import { EmptyStateCard } from "@pages/AdminDashboard/ui/DataTable";
import { TextField } from "@pages/AdminDashboard/ui/TextField";
import { getCasesByUser, searchUserByEmail } from "@store/admin/adminAction";
import { AdminUserProfile, UserSearchStatus } from "@types-runway/admin";
import { Case } from "@types-runway/case";
import { RootState } from "@types-runway/state";

import { findLatestOrder } from "./accountOrders";
import { LatestOrderCard } from "./LatestOrderCard";
import UserAddresses from "./UserAddresses";
import { CasesAndOrders } from "./CasesAndOrders";
import { OpenedFromBar } from "./OpenedFromBar";
import UserProfileCard from "./UserProfileCard";

const { color, radius, shadow, typeScale } = adminTokens;
const [, , spaceMd, spaceLg, , spaceXl] = adminTokens.spacing;
const barPaddingY = 14;
const welcomeTitleSize = "16px";

const Page = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spaceLg}px;
`;

const SearchBar = styled.form<{ $isPhone: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spaceMd}px;
  box-sizing: border-box;
  padding: ${barPaddingY}px ${spaceLg}px;
  background-color: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.card}px;
  box-shadow: ${shadow.card};
  ${({ $isPhone }) => $isPhone && phoneSearchFormLayout}
`;

const EmailSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

const Welcome = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: ${spaceXl}px 0;
  text-align: center;
  color: ${color.inkSoft};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
`;

const WelcomeDetail = styled.span`
  color: ${color.inkSoft};
`;

const WelcomeTitle = styled.span`
  color: ${color.navy};
  font-size: ${welcomeTitleSize};
`;

// On a phone the profile and the saved addresses stand in one column (FIND-R20).
const twoColumns = `
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;

const oneColumn = `
  display: flex;
  flex-direction: column;
`;

const Columns = styled.div<{ $isPhone: boolean }>`
  ${({ $isPhone }) => ($isPhone ? oneColumn : twoColumns)}
  gap: ${spaceLg}px;
`;

interface UserSearchProps {
  // The email the page address holds for this search; `entryKey` changes with every new entry, so
  // entering the same email again searches again (FIND-R18).
  addressEntry?: string | null;
  entryKey?: string;
  // The row of Case Search or Order Search the account was opened from, if any (ACCT-R16, R17).
  searchOrigin?: SearchOrigin | null;
}

const UserSearch = ({ addressEntry = null, entryKey, searchOrigin = null }: UserSearchProps) => {
  const isPhone = useIsPhoneLayout();
  const dispatch = useDispatch();
  const [email, setEmail] = useAdminDraft("user.email", "");
  const [searchedEmail, setSearchedEmail] = useAdminDraft<string | null>(
    "user.searchedEmail",
    null
  );

  const userSearchStatus = useSelector<RootState, UserSearchStatus>(
    (state) => state.admin.userSearchStatus
  );
  const profile = useSelector<RootState, AdminUserProfile>(
    (state) => state.admin.users as unknown as AdminUserProfile
  );
  const cases = useSelector<RootState, Case[]>((state) => state.admin.userCases);

  // The entry the admin searched again from the form; the account then no longer comes from a
  // search row, so it shows no bar and no highlight (ACCT-R16, R17).
  const [formSearchEntryKey, setFormSearchEntryKey] = useState<string | undefined>(undefined);
  const shownOrigin = formSearchEntryKey === entryKey ? null : searchOrigin;

  const latestOrder = findLatestOrder(cases);
  const isPending = userSearchStatus === "pending";
  const hasSearched = searchedEmail !== null;
  // Only trust the profile in state when it answers the search that was just
  // made — otherwise a "not found" search would render the previous match.
  const hasResult = userSearchStatus === "found" && profile?.email === searchedEmail;

  const runSearch = () => {
    setSearchedEmail(email);
    dispatch(searchUserByEmail({ email }));
  };

  useEffect(() => {
    if (!addressEntry) return;

    setEmail(addressEntry);
    setSearchedEmail(addressEntry);
    dispatch(searchUserByEmail({ email: addressEntry }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressEntry, entryKey]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isPending || !email) return;
    setFormSearchEntryKey(entryKey);
    runSearch();
  };

  useEffect(() => {
    if (hasResult) {
      dispatch(getCasesByUser(profile.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResult, profile?.id]);

  return (
    <Page>
      <SearchBar $isPhone={isPhone} role="search" onSubmit={submitSearch}>
        <EmailSlot>
          <TextField
            id="admin-user-search-email"
            label="Email"
            isLabelHidden
            type="email"
            placeholder="Email"
            value={email}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
          />
        </EmailSlot>
        <Button
          look="primary"
          type="submit"
          disabled={isPending || !email}
          isPending={isPending}
          pendingLabel="Search"
        >
          Search
        </Button>
      </SearchBar>

      {!hasSearched && (
        <Welcome>
          <WelcomeTitle>Search by the email the User signed up with</WelcomeTitle>
          <WelcomeDetail>
            The account opens here: profile, saved addresses, Cases and every Order with its status.
          </WelcomeDetail>
        </Welcome>
      )}

      {hasSearched && userSearchStatus === "not_found" && (
        <div data-testid="user-not-found" role="alert" aria-live="assertive">
          <EmptyStateCard
            title="User not found"
            message={`No User has the email ${searchedEmail}. Check the spelling.`}
          />
        </div>
      )}

      {userSearchStatus === "error" && (
        <Banner
          tone="error"
          title="The user search failed."
          body="Nothing changed. Try again."
          action={{ label: "Try again", onAction: runSearch }}
        />
      )}

      {/* The account (ACCT-R1, USER-R10): the Latest order card, the profile and saved addresses,
          then the Cases. */}
      {hasResult && (
        <>
          {shownOrigin && <OpenedFromBar origin={shownOrigin} />}
          {latestOrder && <LatestOrderCard latest={latestOrder} cases={cases} />}
          <Columns $isPhone={isPhone}>
            <UserProfileCard profile={profile} />
            <UserAddresses
              key={profile.id}
              userId={profile.id}
              addresses={profile.addresses ?? []}
            />
          </Columns>
          <CasesAndOrders cases={cases} searched={shownOrigin} />
        </>
      )}
    </Page>
  );
};

export default UserSearch;
